import { useEffect, useRef, type ReactNode } from "react";

/** Native `<dialog>` wrapper: focus trap, Escape, and backdrop come from the platform. */
export function Modal(props: {
  open: boolean;
  title: string;
  onClose(): void;
  children: ReactNode;
  size?: "regular" | "wide";
}) {
  const ref = useRef<HTMLDialogElement | null>(null);
  // A close driven by the `open` prop must not re-enter onClose: the owner may already have
  // moved on to another dialog, and the native close event would clobber that state.
  const closingFromProp = useRef(false);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (props.open && !dialog.open) {
      dialog.showModal();
      // React never renders the `autofocus` attribute, so the platform's dialog focusing steps
      // land on the × button; move focus to the field the dialog marked instead.
      dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    }
    if (!props.open && dialog.open) { closingFromProp.current = true; dialog.close(); }
  }, [props.open]);
  return <dialog
    ref={ref}
    className={`modal modal-${props.size ?? "regular"}`}
    onCancel={(event) => { event.preventDefault(); props.onClose(); }}
    onClose={() => {
      if (closingFromProp.current) { closingFromProp.current = false; return; }
      props.onClose();
    }}
    aria-label={props.title}
  >
    {props.open && <div className="modal-body">
      <header className="modal-header">
        <h3>{props.title}</h3>
        <button type="button" className="modal-close" aria-label="Close" onClick={props.onClose}>×</button>
      </header>
      {props.children}
    </div>}
  </dialog>;
}
